import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { listingPath } from "@/lib/businessSlug";
import SectionHead from "@/components/yts/SectionHead";
import RailArrows from "@/components/yts/RailArrows";
import { Section } from "@/components/yts/HomeSections";
import { useRailScroll } from "@/lib/useRailScroll";
import { Image } from "@/components/ui/image";

/**
 * Editorial event discovery: the lead flyer is a venue-linked karaoke feature;
 * the other cards lead into the island calendar rather than inventing dates.
 */
export default function FeaturedEventsRail({ listings = [] }) {
  const { lang } = useLang();
  const { ref, nudge } = useRailScroll();

  const venue = useMemo(
    () => listings.find((item) => /la verg[uü]enza/i.test(item?.name || "")),
    [listings]
  );
  const liveMusic = useMemo(
    () => listings.find((item) => /live music|music venue|music & records|nightlife|rooftop/i.test(`${item?.subcategory || ""} ${item?.category || ""} ${item?.name || ""}`) && item?.cover_image),
    [listings]
  );
  const market = useMemo(
    () => listings.find((item) => /market|artisan|culture|gallery/i.test(`${item?.subcategory || ""} ${item?.category || ""} ${item?.name || ""}`) && item?.cover_image && item.id !== venue?.id),
    [listings, venue?.id]
  );

  const cards = [
    {
      id: "sunset-salsa-loop",
      title: "Sunset Salsa / Paseo Caribe",
      eyebrow: lang === "es" ? "Serie de salsa · fechas por confirmar" : "Salsa series · dates to be announced",
      image: "",
      to: "/sunset-salsa-paseo-caribe",
      style: "sunset",
      cta: lang === "es" ? "Conoce la experiencia" : "Explore the series",
    },
    {
      id: "karaoke-la-verguenza",
      title: lang === "es" ? "Karaoke en La Vergüenza" : "Karaoke at La Vergüenza",
      eyebrow: lang === "es" ? "Lugar destacado · Condado" : "Venue pick · Condado",
      image: venue?.cover_image,
      to: venue ? listingPath(venue) : "/events",
      style: "karaoke",
      cta: lang === "es" ? "Ver el lugar" : "Explore the venue",
    },
    {
      id: "live-music-island",
      title: lang === "es" ? "Música en vivo por la isla" : "Live Music Across the Island",
      eyebrow: lang === "es" ? "Calendario de eventos" : "Event calendar",
      image: liveMusic?.cover_image,
      to: "/events",
      style: "music",
      cta: lang === "es" ? "Ver próximos eventos" : "View upcoming events",
    },
    {
      id: "markets-culture",
      title: lang === "es" ? "Mercados, cultura y comunidad" : "Markets, Culture & Community",
      eyebrow: lang === "es" ? "Guía de cultura local" : "Local culture guide",
      image: market?.cover_image,
      to: "/map",
      style: "culture",
      cta: lang === "es" ? "Explorar el mapa" : "Explore the map",
    },
  ];

  return (
    <Section>
      <div>
        <SectionHead
          title={lang === "es" ? "ESTA SEMANA." : "THIS WEEK."}
          subtitle={lang === "es" ? "Explora lugares locales y consulta el calendario para fechas confirmadas." : "Explore local venue picks, then check the calendar for confirmed event dates."}
          action={<RailArrows onPrev={() => nudge(-1)} onNext={() => nudge(1)} />}
        />
        <div ref={ref} className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:gap-5 sm:px-0">
          {cards.map((card, index) => (
            <Link
              key={card.id}
              to={card.to}
              className="group relative block w-[min(62vw,216px)] shrink-0 snap-start overflow-hidden rounded-[3px] border border-black/[0.08] bg-[#171717] shadow-[0_5px_18px_rgba(20,18,14,0.07)] transition duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(20,18,14,0.12)] sm:w-[220px]"
            >
              <div className="relative aspect-square overflow-hidden">
                {card.image ? (
                  <Image src={card.image} alt="" fittingType="fill" loading="lazy" decoding="async" quality={74} className="absolute inset-0 h-full w-full object-cover opacity-80 transition duration-500 group-hover:scale-[1.035] group-hover:opacity-90" />
                ) : (
                  <div className={`absolute inset-0 ${card.style === "karaoke" ? "bg-[radial-gradient(ellipse_at_70%_20%,#9e2834_0%,#32171e_42%,#111111_100%)]" : card.style === "music" ? "bg-[radial-gradient(ellipse_at_25%_25%,#355c66_0%,#17282d_48%,#101010_100%)]" : "bg-[radial-gradient(ellipse_at_75%_25%,#b08c58_0%,#4c3b28_42%,#151515_100%)]"}`} />
                )}
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/90" />
                <div className="absolute inset-x-0 top-0 p-3">
                  <div className="inline-flex items-center gap-2 rounded-[2px] border border-white/25 bg-black/25 px-2.5 py-1.5 text-[8px] font-black uppercase tracking-[0.16em] text-white backdrop-blur-md">
                    <span className={index === 0 ? "h-1.5 w-1.5 rounded-full bg-[#ef3340] shadow-[0_0_8px_rgba(239,51,64,0.8)]" : "h-1.5 w-1.5 rounded-full bg-white/80"} />
                    {card.eyebrow}
                  </div>
                </div>
                <div className="absolute inset-x-0 bottom-0 p-3">
                  
                  <h3 className="max-w-[230px] font-serif text-[clamp(22px,4vw,29px)] font-bold uppercase leading-[1.02] tracking-[-0.04em] text-white line-clamp-3 min-h-[2.2em]">{card.title}</h3>
                  <div className="mt-2.5 flex items-center justify-between border-t border-white/25 pt-2.5 text-[9px] font-bold uppercase tracking-[0.1em] text-white/90">
                    <span>{card.cta}</span>
                    <span className="grid h-7 w-7 place-items-center rounded-full border border-white/40 bg-white/10 text-base transition group-hover:bg-white group-hover:text-black">↗</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </Section>
  );
}
